import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      if (error.response?.status === 400) {
        setInvalidToken(true);
        toast.error('Invalid or expired reset link');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (invalidToken) {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="reset-password-invalid">
        <div className="max-w-md mx-auto px-6 md:px-12 py-24 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-4">Invalid Link</h1>
          <p className="text-[#999999] mb-8">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#C9A030] rounded-none py-4 px-8">
              Request New Link
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="reset-password-success">
        <div className="max-w-md mx-auto px-6 md:px-12 py-24 text-center">
          <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-4">Password Reset!</h1>
          <p className="text-[#999999] mb-8">
            Your password has been successfully reset. You can now login with your new password.
          </p>
          <Link to="/login">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#C9A030] rounded-none py-4 px-8">
              Sign In
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="reset-password-page">
      <div className="max-w-md mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Reset Password</h1>
          <p className="text-[#999999]">Enter your new password below.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6 pr-12"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Confirm Password
            </label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6"
              data-testid="confirm-password-input"
            />
          </div>

          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#C9A030] rounded-none py-6 text-sm uppercase tracking-widest font-semibold"
            data-testid="reset-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner mr-2" />
                Resetting...
              </span>
            ) : (
              <>
                Reset Password
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
